import React, { useEffect, useRef } from "react";
import { Button, notification, Result, Space } from "antd";
import {
  CheckOutlined,
  CoffeeOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import { docsUrl } from "services/region/region";

import { SubscriptionItem } from "components/subscription-item/SubscriptionItem";

import { useProfileSubscriptions } from "services/rest/subscription";
import { isYookassa } from "services/payment/paymentProvider";

import { StoreList } from "toolkit/list/StoreList";

import { NavigateButton } from "components/navigate-button/NavigateButton";
import { StoreCard } from "toolkit/card/StoreCard";
import { useCheckTrial, useGenerateTrial } from "services/rest/account";

import { useIntl } from "react-intl";

import { StoreError } from "pages/system/error/StoreError";

import { HelpLink } from "components/help-link/HelpLink";
import "./SubscriptionListCard.less";

const PENDING_PAYMENT_POLL_INTERVAL_MS = 5000;
const PENDING_PAYMENT_POLL_LIMIT = 24;

export function SubscriptionListCard() {
  const intl = useIntl();
  const { response, loading, error, fetch } = useProfileSubscriptions();

  // YooKassa mode: a purchase returns from the payment page before the
  // payment.succeeded webhook lands, so PAYMENT_DUE subscriptions are polled
  // until they activate (bounded to avoid endless polling of failed payments)
  const pollCount = useRef(0);
  const hasPendingPayment =
    isYookassa() &&
    !!response?.some((subscription) => subscription.status === "PAYMENT_DUE");
  useEffect(() => {
    if (!hasPendingPayment || pollCount.current >= PENDING_PAYMENT_POLL_LIMIT) {
      return;
    }
    const timer = setTimeout(() => {
      pollCount.current += 1;
      fetch();
    }, PENDING_PAYMENT_POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [hasPendingPayment, response]);
  const {
    response: checkTrialResponse,
    loading: checkTrialLoading,
    error: checkTrialError,
    fetch: checkTrialFetch,
  } = useCheckTrial();

  const isAvailableTrial =
    !checkTrialLoading &&
    !checkTrialError &&
    checkTrialResponse &&
    checkTrialResponse.available;
  const isEmpty = !response || response.length <= 0;

  const content: React.ReactNode = isEmpty ? (
    <Result
      icon={<CoffeeOutlined style={{ color: "#DEE8ED" }} />}
      extra={intl.formatMessage({
        id: "jmix-store.subscription-list-card.empty-description",
      })}
    />
  ) : (
    response?.map((subscription) => {
      return (
        <SubscriptionItem
          key={subscription.id}
          subscription={subscription}
          onAfter={fetch}
        />
      );
    })
  );

  return (
    <StoreCard
      bordered={true}
      className="subscription-list-card"
      title={intl.formatMessage({
        id: "jmix-store.subscription-list-card.title",
      })}
      extra={
        <Space>
          <HelpLink name="jmix-store.help-link.need-help" to={docsUrl("/jmix/account-management.html#subscription-management")}/>
          {
            isAvailableTrial && (
            <GetTrialButton
              onAfter={() => {
                fetch();
                checkTrialFetch();
              }}
            />
          )}
          <NavigateButton
            icon={<ShoppingCartOutlined />}
            ghost={false}
            size="small"
            name={intl.formatMessage({
              id: "jmix-store.subscription-list-card.buy-button",
            })}
            path="/store/subscription"
          />
        </Space>
      }
    >
      <StoreList
        loading={loading}
      >
        {
          error
            ? <StoreError
              reload={fetch}
              autoHeight={true}
              bordered={false}
            />
            : content
        }
      </StoreList>
    </StoreCard>
  );
}

type GetTrialButtonProps = {
  onAfter: () => void,
}

function GetTrialButton(props: GetTrialButtonProps) {
  const { onAfter } = props;
  const intl = useIntl();
  const { fetch, loading } = useGenerateTrial();

  return (
    <Button
      size="small"
      type="primary"
      ghost
      icon={<CheckOutlined />}
      loading={loading}
      onClick={() => {
        fetch()
          .then(() => {
            onAfter();
          })
          .catch(() => {
            notification.error({
              message: intl.formatMessage({ id: "jmix-store.subscription-list-card.get-trial-error" }),
            });
          });
      }}
    >
      {intl.formatMessage({ id: "jmix-store.subscription-list-card.get-trial-button" })}
    </Button>
  );
}
